/**
 * Meant to take in our date-only strings in the format of '2025-10-15' and return
 * display data. This function assumes that you want to use the current browser's
 * timezone.
 */
export const getLocalTimezoneDisplayDataFromDateString = (input: {
  date: string;
  format: 'short' | 'long';
}): {
  dayOfWeek: string;
  day: number;
  month: string;
} => {
  const { date, format } = input;

  // Validate date string format: YYYY-MM-DD
  if (!/^\d{4}-\d{2}-\d{2}$/.test(date)) {
    throw new Error(
      `Date string must be in the format 'YYYY-MM-DD', got ${date}`,
    );
  }

  const dateObj = new Date(date + 'T00:00:00');
  const dayOfWeek = dateObj.toLocaleDateString(undefined, { weekday: format });
  const day = dateObj.getDate();
  const month = dateObj.toLocaleDateString(undefined, { month: format });

  return {
    dayOfWeek,
    day,
    month,
  };
};

/**
 * Used to take in UTC DateTime strings (as in those who end with Z) like:
 * '2025-10-10T07:00:00.000Z', converts them to the user's local timezone, and
 * returns display data.
 */
export const getLocalDisplayDataFromDateObject = (input: {
  date: string;
  format: 'short' | 'long';
}): {
  dayOfWeek: string;
  day: number;
  month: string;
  hour: number;
  minute: string;
  ampm: 'AM' | 'PM';
} => {
  const { date, format } = input;

  if (!/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d+)?Z$/.test(date)) {
    throw new Error(
      "Date string must be a UTC datetime in the format 'YYYY-MM-DDTHH:mm:ss(.sss)Z'",
    );
  }

  const dateObj = new Date(date);
  const dayOfWeek = dateObj.toLocaleDateString(undefined, { weekday: format });
  const day = dateObj.getDate();
  const month = dateObj.toLocaleDateString(undefined, { month: format });

  let hour = dateObj.getHours();
  const minute = dateObj.getMinutes().toString().padStart(2, '0');
  const ampm = hour >= 12 ? 'PM' : 'AM';
  hour = hour % 12 || 12;

  return {
    dayOfWeek,
    day,
    month,
    hour,
    minute,
    ampm,
  };
};

/** Formats a date string (YYYY-MM-DD) into a header string like "Monday, January 1" */
export const headerFormatter = (date: string): string => {
  const { dayOfWeek, day, month } = getLocalTimezoneDisplayDataFromDateString({
    date,
    format: 'long',
  });

  return `${dayOfWeek}, ${month} ${day}`;
};
